import React, { useEffect, useRef, useState } from 'react';

// Realtor Mode — multi-measurement SVG overlay for the web viewer.
//
// ResizeObserver tracks the rendered pixel size of the live image container
// so normalized host coordinates (0–1) remap correctly to viewer pixels at
// any browser window size.
//
// Props:
//   measurements — array of { id, points: [{x,y},{x,y}], label } | []
export default function MeasurementOverlay({ measurements }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const hasOverlay = measurements && measurements.length > 0 && size.width > 0;

  if (!hasOverlay) {
    return <div ref={containerRef} style={overlayStyle} />;
  }

  return (
    <div ref={containerRef} style={overlayStyle}>
      <svg width={size.width} height={size.height} style={{ display: 'block', pointerEvents: 'none' }}>
        {measurements.map((m) => {
          const x1 = m.points[0].x * size.width,  y1 = m.points[0].y * size.height;
          const x2 = m.points[1].x * size.width,  y2 = m.points[1].y * size.height;
          const midX = (x1 + x2) / 2,             midY = (y1 + y2) / 2;
          const labelBoxW = Math.max(70, (m.label?.length ?? 0) * 8 + 24);
          return (
            <React.Fragment key={m.id}>
              <line x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#00D4FF" strokeWidth={2.5} strokeDasharray="8,4" strokeLinecap="round" />
              <circle cx={x1} cy={y1} r={7} fill="#00D4FF" opacity={0.92} />
              <circle cx={x2} cy={y2} r={7} fill="#00D4FF" opacity={0.92} />
              {m.label?.length > 0 && (
                <>
                  <rect x={midX - labelBoxW / 2} y={midY - 13} width={labelBoxW} height={26} rx={6} fill="rgba(0,0,0,0.78)" />
                  <text x={midX} y={midY + 5} textAnchor="middle" fill="white" fontSize={13} fontWeight="bold"
                    fontFamily="system-ui, -apple-system, sans-serif">
                    {m.label}
                  </text>
                </>
              )}
            </React.Fragment>
          );
        })}
      </svg>
    </div>
  );
}

const overlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
};
